"use client";

/**
 * `<TickerBar>` — horizontal scrolling marketing ticker.
 *
 * Fetches /api/marketing/ticker on mount. While the request is in flight or
 * if it fails / returns empty, renders a static fallback strip so the
 * homepage never shows a blank slot above the hero.
 *
 * The scroll animation duplicates the items so `translateX(-50%)` loops
 * seamlessly — the `wtm-ticker-scroll` keyframe is declared in
 * `src/styles/design-tokens.css`.
 */

import { useEffect, useState } from "react";
import type { MarketingTickerItem } from "@/app/api/marketing/ticker/route";

const FALLBACK_ITEMS: MarketingTickerItem[] = [
  { symbol: "SPY", displayName: "S&P 500", price: 0, changePercent: 0 },
  { symbol: "QQQ", displayName: "NASDAQ", price: 0, changePercent: 0 },
  { symbol: "NVDA", displayName: "NVDA", price: 0, changePercent: 0 },
  { symbol: "AAPL", displayName: "AAPL", price: 0, changePercent: 0 },
  { symbol: "MSFT", displayName: "MSFT", price: 0, changePercent: 0 },
];

function formatPrice(p: number): string {
  if (p === 0) return "-";
  if (p >= 1000) return p.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return p.toFixed(2);
}

function formatPercent(p: number): string {
  if (p === 0) return "-";
  const sign = p > 0 ? "+" : "";
  return `${sign}${p.toFixed(2)}%`;
}

function TickerItem({ item }: { item: MarketingTickerItem }) {
  const pct = item.changePercent;
  const color =
    pct === 0
      ? "var(--color-muted)"
      : pct > 0
        ? "var(--color-green)"
        : "var(--color-red)";

  return (
    <span
      className="inline-flex items-center gap-2"
      style={{
        padding: "0 24px",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ color: "var(--color-text)", fontWeight: 500 }}>
        {item.displayName}
      </span>
      <span style={{ color: "var(--color-muted)" }}>
        {formatPrice(item.price)}
      </span>
      <span style={{ color }}>{formatPercent(item.changePercent)}</span>
    </span>
  );
}

export function TickerBar() {
  const [items, setItems] = useState<MarketingTickerItem[] | null>(null);
  const [tabVisible, setTabVisible] = useState(true);

  useEffect(() => {
    let alive = true;

    const runFetch = () => {
      if (!alive) return;
      fetch("/api/marketing/ticker")
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data: { items: MarketingTickerItem[] }) => {
          if (!alive) return;
          setItems(data.items?.length ? data.items : FALLBACK_ITEMS);
        })
        .catch(() => {
          if (!alive) return;
          setItems(FALLBACK_ITEMS);
        });
    };

    // Defer the ticker fetch until the browser is idle. The visual
    // fallback ("S&P 500 · — · —") already covers the gap, and the
    // hero is what should win network bandwidth on first paint.
    // requestIdleCallback isn't available on Safari < 17 — fall back
    // to a short setTimeout so the fetch still fires.
    type IdleHandle = number;
    type IdleCallback = (
      cb: IdleRequestCallback,
      opts?: { timeout: number },
    ) => IdleHandle;
    const ric = (
      window as unknown as { requestIdleCallback?: IdleCallback }
    ).requestIdleCallback;
    const cic = (
      window as unknown as { cancelIdleCallback?: (h: IdleHandle) => void }
    ).cancelIdleCallback;

    if (typeof ric === "function") {
      const handle = ric(runFetch, { timeout: 2000 });
      return () => {
        alive = false;
        if (typeof cic === "function") cic(handle);
      };
    }
    const t = setTimeout(runFetch, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    onVis();
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const display = items ?? FALLBACK_ITEMS;
  // Duplicate the strip so translateX(-50%) loops without a visible gap.
  const doubled = [...display, ...display];

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        width: "100%",
        height: 32,
        borderBottom: "1px solid var(--color-line-2)",
        background: "rgba(10,14,26,0.82)",
        backdropFilter: "blur(20px) saturate(140%)",
        WebkitBackdropFilter: "blur(20px) saturate(140%)",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
      }}
      aria-label="Live market ticker"
    >
      <div
        aria-hidden="true"
        style={{
          display: "inline-flex",
          animation: "wtm-ticker-scroll 60s linear infinite",
          animationPlayState: tabVisible ? "running" : "paused",
          willChange: "transform",
        }}
      >
        {doubled.map((item, idx) => (
          <TickerItem key={`${item.symbol}-${idx}`} item={item} />
        ))}
      </div>
    </div>
  );
}
